import HtmlCleaner from '@/helpers/HtmlCleaner'
import type { TGDocHtmlResult } from '@/types/remote-imports'

interface GDocResult {
  source: string
  title: string
  content: string | null
  websiteUrl: string
  domain: string
  thumbnail: string
}

const gDocIdRegex =
  /^https:\/\/docs\.google\.com\/document\/(?:(?:u\/\d+\/)?d\/([A-Za-z0-9_-]+)(?:[/?#]|$)|pub\?(?:[^#]*&)?id=([A-Za-z0-9_-]+))/
const publishedIdSegment = 'e'
const publishedDocRegex = /^https:\/\/docs\.google\.com\/document\/d\/e\/[A-Za-z0-9_-]+\/pub(?:[?#]|$)/

export class GDocImport {
  isGDocUrl(url: string): boolean {
    if (!url) return false
    url = url.trim()
    return url.startsWith('https://docs.google.com') && !this.isPublishedGDocUrl(url)
  }

  isPublishedGDocUrl(url: string): boolean {
    return publishedDocRegex.test(url.trim())
  }

  handleGDocResHelper(
    html: string | null,
    msg: string = 'Error loading google document',
    url: string,
  ): GDocResult | null {
    if (!html) {
      window.alert(msg) // Using window.alert as a simple replacement for alertify
      return null
    }
    return {
      source: 'url',
      title: '',
      content: html,
      websiteUrl: url,
      domain: '',
      thumbnail: '',
    }
  }

  getDocIdFromUrl(url: string): string {
    const match = url.trim().match(gDocIdRegex)
    const id = match?.[1] ?? match?.[2]
    if (!id || id === publishedIdSegment) {
      throw new Error(`Could not handle this Google docs url format: ${url}`)
    }

    return id
  }

  getGoogleDocProxyUrl(url: string, baseUrl: string): string {
    const id = this.getDocIdFromUrl(url)
    return `${baseUrl}/google_doc/${id}`
  }

  // getGoogleDocDownloadAsHtml(url: string): string {
  //   const id = this.getDocIdFromUrl(url)
  //   return `https://docs.google.com/document/export?format=html&id=${id}`
  // }

  setupIframe(iframeId: string): HTMLIFrameElement {
    let iframe = document.getElementById(iframeId) as HTMLIFrameElement | null
    if (!iframe) {
      iframe = document.createElement('iframe')
      iframe.id = iframeId
      document.body.appendChild(iframe)
      console.log('Created new iframe')
    }
    return iframe
  }

  async getHtmlFromGDocValidUrl(
    url: string,
    baseUrl: string,
    signal?: AbortSignal,
  ): Promise<TGDocHtmlResult> {
    return new Promise((resolve, reject) => {
      // Wrap the function logic in a new Promise
      const proxyUrl = this.getGoogleDocProxyUrl(url, baseUrl)
      const iframe = this.setupIframe('gdoc-iframe')
      if (signal?.aborted) {
        resolve({ html: null })
        return
      }
      const onAbort = () => {
        iframe.onload = null
        iframe.src = ''
        resolve({ html: null })
      }
      signal?.addEventListener('abort', onAbort, { once: true })
      iframe.onload = () => {
        signal?.removeEventListener('abort', onAbort)
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
          if (!iframeDoc) {
            throw new Error('Unable to access iframe document.')
          }
          const body = iframeDoc.body
          const text = body.textContent || ''
          const status = this.getIframeResponseStatus(iframe)
          const redirected = iframe.contentWindow?.location.pathname !== new URL(proxyUrl).pathname
          iframe.src = '' // Clear the iframe src
          if (text.startsWith('{"status":"error","message":"')) {
            console.log(text)
            let msg
            try {
              const res = JSON.parse(text)
              msg = res.message
            } catch {
              msg = 'Error parsing response message.'
            }
            resolve({ html: null, message: msg })
          } else if (redirected || (status !== null && status >= 400)) {
            resolve({ html: null, message: 'Error loading google document', status, redirected })
          } else {
            this.fullDocCleanup(body)
            resolve({ html: body.innerHTML })
          }
        } catch (error) {
          reject(error)
        } finally {
          iframe.onload = null // Remove the onload handler to clean up
        }
      }
      iframe.src = proxyUrl // Trigger the loading
    })
  }

  getIframeResponseStatus(iframe: HTMLIFrameElement): number | null {
    const entries = iframe.contentWindow?.performance.getEntriesByType('navigation') as
      | PerformanceNavigationTiming[]
      | undefined
    const status = entries?.[0]?.responseStatus

    return status ? status : null
  }

  fullDocCleanup(node: HTMLElement): void {
    this.fixImageInsideHeading(node) // ok
    this.inlineTextElementsStyles(node)
    this.unwrapLinksIfNeeded(node) // ok
    this.cleanupLinks(node) // ok
    this.cleanupHeadersAndParagraphs(node) // ok
    HtmlCleaner.cleanupSourceHtml(node)
    HtmlCleaner.handleH1HeadingsIfPresent(node, true)
  }

  fixImageInsideHeading(node: Element): void {
    const images = node.querySelectorAll('h1 img, h2 img, h3 img, h4 img, h5 img, h6 img')
    images.forEach((img) => {
      const heading = img.closest('h1, h2, h3, h4, h5, h6')
      if (heading) {
        heading.parentNode?.insertBefore(img, heading.nextSibling)
      }
    })
  }

  removeUnnecessarySpans(node: Element) {
    const style = node.getAttribute('style')
    node.querySelectorAll('span').forEach((span) => {
      const spanStyle = span.getAttribute('style')?.trim() || ''
      if (spanStyle === '' || style === spanStyle) {
        while (span.firstChild) span.parentNode?.insertBefore(span.firstChild, span)
        span.remove()
      }
    })
  }

  hasJustOneSpan(node: Element, ignoreStyleAndClass: boolean): boolean {
    const children = node.children
    if (children.length !== 1 || node.querySelectorAll('img,a').length > 0) return false
    const child = children[0]!
    const style = child.getAttribute('style')?.trim()
    const _class = child.getAttribute('class')?.trim()
    if (child.tagName !== 'SPAN') return false
    if (!ignoreStyleAndClass && (style || _class)) {
      return false
    }
    return this.validateChildNodes(node.childNodes)
  }

  private validateChildNodes(childNodes: NodeListOf<ChildNode>): boolean {
    let count = 0
    let elementCount = 0
    childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue?.trim()) {
        count++
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        elementCount++
      }
    })
    return count + elementCount <= 1
  }

  // Method to merge similar spans based on their styles
  static mergeSimilarSpans(node: Element): void {
    const spans = Array.from(node.children).filter((child) => child.tagName === 'SPAN')
    if (!spans.length) {
      return
    }
    const styles = spans.map((span) => span.getAttribute('style') || '')
    const matches = this.analyzeArrayElementsEquality(styles)
    const groups = this.groupMatches(matches)
    if (!groups.length) {
      return
    }
    groups.forEach((group) => {
      const intoSpan = spans[group[0]!]!
      for (let i = 1; i < group.length; i++) {
        const span = spans[group[i]!]!
        if (!this.areTrueSiblings(intoSpan, span)) {
          break
        }
        intoSpan.innerHTML += span.innerHTML
        console.log('SPAN combined with previous, TEXT was ' + span.textContent)
        span.parentNode?.removeChild(span)
      }
    })
  }

  // Analyzes array elements for equality in styles
  static analyzeArrayElementsEquality(styles: string[]): number[] {
    const matches = new Array(styles.length).fill(0)
    matches[0] = 0
    for (let i = 1; i < styles.length; i++) {
      matches[i] = styles[i] === styles[i - 1] ? matches[i - 1] : i
    }
    return matches
  }

  // Groups matches based on continuous equality
  static groupMatches(matches: number[]): number[][] {
    const results: number[][] = []
    let current: number[] = []
    for (let i = 0; i < matches.length; i++) {
      if (i < matches.length - 1 && matches[i] === matches[i + 1]) {
        if (current.length === 0) {
          current.push(i)
        }
        current.push(i + 1)
      } else {
        if (current.length !== 0) {
          results.push([...current]) // Clone current to ensure we're pushing a new instance
          current = []
        }
      }
    }
    return results
  }

  // Helper function to check if two nodes are true siblings (adjacent)
  private static areTrueSiblings(a: Element, b: Element): boolean {
    return a.nextSibling === b
  }

  cleanupHeadersAndParagraphs(node: Element) {
    // Handling headers and paragraphs
    node.querySelectorAll(':scope > header, :scope > p').forEach((element) => {
      // Placeholder for HtmlCleaner.mergeSimilarSpans as its implementation details are not provided
      GDocImport.mergeSimilarSpans(element)
    })

    node.querySelectorAll(':scope > p, :scope > li').forEach((element) => {
      this.removeUnnecessarySpans(element)
      if (this.hasJustOneSpan(element, true)) {
        GDocImport.mergeSimilarSpans(element)
      }
    })
  }

  _unwrapLinkIfNeeded(a: HTMLAnchorElement): boolean {
    const parent = a.parentElement
    const tagsWhiteList = ['SPAN', 'U', 'B', 'I']
    if (!parent || !tagsWhiteList.includes(parent.tagName.toUpperCase())) {
      return false
    }
    if (parent.childNodes.length === 1 && parent.parentNode) {
      parent.parentNode.insertBefore(a, parent)
      parent.remove()
      return true
    }
    return false
  }

  unwrapLinksIfNeeded(node: Element): void {
    node.querySelectorAll('a').forEach((a) => {
      this._unwrapLinkIfNeeded(a as HTMLAnchorElement)
    })
  }

  cleanupLinks(node: Element): void {
    node.querySelectorAll('a').forEach((a) => {
      const href = a.getAttribute('href')
      if (href && href.startsWith('https://www.google.com/url?q=')) {
        const url = new URL(href)
        const q = url.searchParams.get('q')
        if (q) {
          a.setAttribute('href', q)
        }
      }
    })
  }

  inlineTextElementsStyles(body: Element): void {
    const computedStyle = getComputedStyle(body)

    const defaultBg = computedStyle.backgroundColor
    const bodyAlign = computedStyle.textAlign
    const defaultAligns = [bodyAlign]
    if (bodyAlign === 'start') {
      defaultAligns.push('left')
    }

    const defaultTextStyles: { [key: string]: string[] } = {
      'font-weight': [computedStyle.fontWeight],
      'font-style': ['normal'],
      'text-decoration': ['none', 'rgb(0, 0, 0)'],
      'text-align': defaultAligns,
      'background-color': [defaultBg],
      'color': [computedStyle.color],
    }

    function isCrapDefaultValue(value: string): boolean {
      const crapDefaultStyleValues = ['rgb(0, 0, 0)', 'rgba(0, 0, 0, 0)']
      return crapDefaultStyleValues.includes(value)
    }

    function styleDifferentThanParent(el: Element, style: string, styleVal: string): boolean {
      const parentStyle = getComputedStyle(el.parentElement!)
      return parentStyle.getPropertyValue(style) !== styleVal
    }

    body.querySelectorAll('p, span, li').forEach((el) => {
      Object.keys(defaultTextStyles).forEach((style) => {
        const styleVal = getComputedStyle(el).getPropertyValue(style)
        if (style === 'text-align' && el.getAttribute('dir') === 'rtl') {
          return
        }
        const defaultStyleValArr = defaultTextStyles[style]!
        if (
          !defaultStyleValArr.includes(styleVal) &&
          !isCrapDefaultValue(styleVal) &&
          styleDifferentThanParent(el, style, styleVal)
        ) {
          const existing = el.getAttribute('style')
          el.setAttribute('style', existing ? `${existing}; ${style}: ${styleVal}` : `${style}: ${styleVal}`)
        }
      })
    })
  }
}
